// Pure, testable helpers for desktop password recovery. No telemetry, no
// logging — callers must never emit the token or password anywhere.

export const MIN_RESET_PASSWORD = 8; // matches existing register policy ("8+ characters")
export const MAX_RESET_PASSWORD = 128; // matches backend maximum

export const FORGOT_NEUTRAL =
  "If an account exists for that email, we've sent a link to reset your password. Check your inbox and spam folder.";

// Read the reset token from a query string WITHOUT consuming it. Returns null
// when absent/blank. Accepts "?token=…" or "token=…".
export function extractResetToken(search) {
  try {
    const raw = String(search || "");
    const qs = raw.startsWith("?") ? raw : `?${raw}`;
    const t = new URLSearchParams(qs).get("token");
    return t && t.trim() ? t.trim() : null;
  } catch {
    return null;
  }
}

export function validateNewPassword(pw, confirm) {
  if (!pw || pw.length < MIN_RESET_PASSWORD) return `Choose a password with at least ${MIN_RESET_PASSWORD} characters.`;
  if (pw.length > MAX_RESET_PASSWORD) return `Password must be ${MAX_RESET_PASSWORD} characters or fewer.`;
  if (confirm != null && pw !== confirm) return "Those passwords don't match.";
  return null;
}

// Classify a reset failure into a UX outcome. Expired / used / invalid tokens
// offer "request a new link"; rate-limit and transport issues offer retry.
export function classifyResetError(err) {
  const msg = String((err && err.message) || err || "").toLowerCase();
  if (/expired/.test(msg)) return { kind: "expired", message: "This reset link has expired.", canRequestNew: true };
  if (/(used|already)/.test(msg)) return { kind: "used", message: "This reset link has already been used.", canRequestNew: true };
  if (/(invalid|not found|no such|bad token|malformed)/.test(msg)) return { kind: "invalid", message: "This reset link is invalid.", canRequestNew: true };
  if (/(rate|too many|429)/.test(msg)) return { kind: "rate", message: "Too many attempts. Please wait a moment and try again.", canRequestNew: false };
  if (/(timed out|network)/.test(msg)) return { kind: "retry", message: (err && err.message) || "Network error. Please try again.", canRequestNew: false };
  return { kind: "retry", message: (err && err.message) || "Something went wrong. Please try again.", canRequestNew: false };
}

// Forgot flow: account-existence errors NEVER surface (stay neutral). Only
// rate-limit / provider / transport failures produce a retryable message.
export function classifyForgotError(err) {
  const msg = String((err && err.message) || err || "").toLowerCase();
  if (/(rate|too many|429)/.test(msg)) return { retry: true, message: "Too many requests. Please wait a moment and try again." };
  if (/(timed out|network|unavailable|failed to fetch|internal|server|50\d|502|503)/.test(msg)) return { retry: true, message: (err && err.message) || "Couldn't send the link right now. Please try again." };
  return { retry: false, message: null }; // neutral success — no existence leak
}
